import Vue from 'vue';
import moment from 'moment';

import SiteService from '@/services/site.service';
import auth from '@/store';
import { Config } from '@/config';

export const sites = {
  namespaced: true,
  state: {
    sites: [],
    destinations: [],
    current_destination: undefined,
    parkings: [],
    current_parking: undefined,
    current_site: undefined,
    attendance: undefined,
    attendance_labels: Config.ATTENDANCE_DEFAULT_LABELS,
    current_spot: undefined,
  },
  actions: {
    get({ commit }) {
      SiteService.get().then(
        sites => {
          commit('getSuccess', sites);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    async getById({ commit }, siteId) {
      return SiteService.getById(siteId).then(
        site => {
          commit('setCurrentSite', site);
        },
        () => {
          commit('getByIdFailure');
        },
      );
    },
    async getParkings({ commit, dispatch }, siteId) {
      return SiteService.getParkings(siteId).then(
        parkings => {
          commit('getParkingsSuccess', parkings);
          dispatch('getParkingsAttendance', parkings);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    getParkingsAttendance({ commit }, parkings) {
      const parkingIds = parkings.map(p => p._id);
      SiteService.getParkingsAttendance(parkingIds).then(
        parkingsAttendance => {
          commit('getParkingsAttendanceSuccess', parkingsAttendance);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    setCurrentParking({ commit }, current_parking) {
      commit('setCurrentParking', current_parking);
    },
    resetCurrentParking({ commit }) {
      commit('setCurrentParking', undefined);
    },
    async getDestinations({ commit }, siteId) {
      return SiteService.getDestinations(siteId).then(
        destinations => {
          commit('getDestinationsSuccess', destinations);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    async getNavigationSites({ commit }, location) {
      return SiteService.getNavigationSites(location.lat, location.lng).then(
        sites => {
          commit('getSuccess', sites.sites);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    async getNavigationParkings({ commit }, location) {
      return SiteService.getNavigationParkings(this.state.sites.current_destination._id, location).then(
        parkings => {
          commit('setParkingsSuccess', parkings);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    async getNavigationSpot({ commit }, location) {
      return SiteService.getNavigationSpot(this.state.sites.current_site.contract, this.state.sites.current_destination._id, this.state.sites.current_parking._id, location).then(
        spot => {
          commit('setSpotSuccess', spot);
        },
        () => {
          commit('getFailure');
        },
      );
    },
    resetState({ commit }) {
      commit('resetState');
    },
  },
  mutations: {
    getSuccess(state, sites) {
      if (auth.getters['auth/settings'] !== null) {
        sites = sites.map(site => {
          if (auth.getters['auth/settings'].config !== undefined && auth.getters['auth/settings'].config.sites !== undefined && auth.getters['auth/settings'].config.sites[site._id] !== undefined) {
            return { ...site, ...auth.getters['auth/settings'].config.sites[site._id] };
          }
        });
      }
      Vue.set(state, 'sites', sites);
    },
    getFailure(state) {
      Vue.set(state, 'sites', []);
    },
    setCurrentSite(state, site) {
      if (auth.getters['auth/settings'] !== null && auth.getters['auth/settings'].config.sites[site._id] !== undefined) {
        site = { ...site, ...auth.getters['auth/settings'].config.sites[site._id] };
      }
      Vue.set(state, 'current_site', site);
    },
    getParkingsAttendanceSuccess(state, parkingsAttendance) {
      //Compute nb spots
      let totalSlots = 0;
      parkingsAttendance.parkings.forEach(parking => {
        totalSlots += parking.nb_spaces;
      });
      const attendance = parkingsAttendance.attendance;
      const isoDow = moment().isoWeekday();

      let newAttendance = [];
      let newAttendanceLabels = [];

      if (state.current_site.opening_hours[isoDow][0] && state.current_site.opening_hours[isoDow][1]) {
        const openingHoursStart = state.current_site.opening_hours[isoDow][0].split(':')[0];
        const openingHoursEnd = state.current_site.opening_hours[isoDow][1].split(':')[0];

        let tempAttendance = [];
        for (let i = parseInt(openingHoursStart, 10); i <= parseInt(openingHoursEnd, 10); i++) {
          const element = attendance[i];
          tempAttendance.push((element / totalSlots) * 100);
        }

        let tempAttendanceLabels = Config.ATTENDANCE_DEFAULT_LABELS.slice(openingHoursStart, parseInt(openingHoursEnd) + 1);

        // Add data to smooth graph
        for (let i = 0; i < tempAttendance.length; i++) {
          newAttendance.push(tempAttendance[i]);
          newAttendanceLabels.push(tempAttendanceLabels[i]);

          if (tempAttendance[i + 1] !== undefined) {
            const totalDelta = tempAttendance[i + 1] - tempAttendance[i];
            const delta = totalDelta / (Config.ATTENDANCE_SMOOTH_COEF + 1);
            for (let e = 1; e <= Config.ATTENDANCE_SMOOTH_COEF; e++) {
              newAttendance.push(tempAttendance[i] + e * delta);
              newAttendanceLabels.push('');
            }
          }
        }
      }
      Vue.set(state, 'attendance_labels', newAttendanceLabels);
      Vue.set(state, 'attendance', newAttendance);
    },
    async getParkingsSuccess(state, parkings) {
      parkings = parkings.map((parking, index) => {
        const geoJson = {
          id: index,
          type: 'Feature',
          properties: {
            fillcolor: '#fff253',
          },
          geometry: {
            type: 'Polygon',
            coordinates: [
              parking.polygon.map(point => {
                return [point.lng, point.lat];
              }),
            ],
          },
        };
        const canvas = {
          type: 'canvas',
          canvas: 'canvas' + index,
          coordinates: [],
          // Set to true if the canvas source is animated. If the canvas is static, animate should be set to false to improve performance.
          animate: false,
        };

        return { ...parking, geoJson, canvas };
      });

      if (auth.getters['auth/settings'] !== null) {
        parkings = parkings.map(parking => {
          if (
            auth.getters['auth/settings'].config !== undefined &&
            auth.getters['auth/settings'].config.parkings !== undefined &&
            auth.getters['auth/settings'].config.parkings[parking._id] !== undefined
          ) {
            if (auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner !== undefined) {
              const deltaLat = 0.0006;
              const deltaLng = 0.00095;
              parking.canvas.coordinates.push([
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lng,
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lat + deltaLat,
              ]);
              parking.canvas.coordinates.push([
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lng + deltaLng,
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lat + deltaLat,
              ]);
              parking.canvas.coordinates.push([
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lng + deltaLng,
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lat,
              ]);
              parking.canvas.coordinates.push([
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lng,
                auth.getters['auth/settings'].config.parkings[parking._id].location_attendance_bar_top_left_corner.lat,
              ]);
            }
            return { ...parking, ...auth.getters['auth/settings'].config.parkings[parking._id] };
          }
          return parking;
        });
      }
      Vue.set(state, 'parkings', parkings);
    },
    setParkingsSuccess(state, parkings) {
      const parkingsMap = {};
      parkings.forEach(parking => {
        let color = Config.ATTENDANCE_BAR.GREEN.COLOR;
        if (parking.attendance_percentage !== undefined && parking.attendance_percentage >= Config.ATTENDANCE_BAR.YELLOW.VALUE && parking.attendance_percentage < Config.ATTENDANCE_BAR.ORANGE.VALUE) {
          color = Config.ATTENDANCE_BAR.YELLOW.COLOR;
        } else if (
          parking.attendance_percentage !== undefined &&
          parking.attendance_percentage >= Config.ATTENDANCE_BAR.ORANGE.VALUE &&
          parking.attendance_percentage < Config.ATTENDANCE_BAR.RED.VALUE
        ) {
          color = Config.ATTENDANCE_BAR.ORANGE.COLOR;
        } else if (parking.attendance_percentage !== undefined && parking.attendance_percentage > Config.ATTENDANCE_BAR.RED.VALUE) {
          color = Config.ATTENDANCE_BAR.RED.COLOR;
        }
        parking.score_color = color;
        parkingsMap[parking._id] = parking;
      });
      Vue.set(
        state,
        'parkings',
        state.parkings.map(parking => {
          if (parkingsMap[parking._id]) {
            return { ...parking, ...parkingsMap[parking._id] };
          }
          return parking;
        }),
      );
    },
    setCurrentParking(state, current_parking) {
      Vue.set(state, 'current_parking', current_parking);
    },
    getDestinationsSuccess(state, destinations) {
      destinations = destinations.map((destination, index) => {
        let geoJson = {
          id: index,
          type: 'Feature',
          properties: {
            fillcolor: '#fff253',
          },
          geometry: {
            type: 'Polygon',
            coordinates: [
              destination.polygon.map(point => {
                return [point.lng, point.lat];
              }),
            ],
          },
        };
        return { ...destination, geoJson: geoJson };
      });

      if (auth.getters['auth/settings'] !== null) {
        destinations = destinations.map(destination => {
          if (
            auth.getters['auth/settings'].config !== undefined &&
            auth.getters['auth/settings'].config.destinations !== undefined &&
            auth.getters['auth/settings'].config.destinations[destination._id] !== undefined
          ) {
            return { ...destination, ...auth.getters['auth/settings'].config.destinations[destination._id] };
          }
          return destination;
        });
      }
      Vue.set(state, 'destinations', destinations);
      Vue.set(state, 'current_destination', destinations[0]);
    },
    getByIdFailure(state) {
      Vue.set(state, 'current_site', undefined);
    },
    setSpotSuccess(state, spot) {
      Vue.set(state, 'current_spot', spot);
    },
    resetState(state) {
      state.sites = [];
      state.destinations = [];
      state.parkings = [];
      state.current_site = undefined;
      state.current_destination = undefined;
      state.attendance = undefined;
      state.current_spot = undefined;
    },
  },
  getters: {},
};
